'use client';
import React from 'react';
import Link from 'next/link';
import { IRepository } from '@/types';
import { Identity } from '@/hooks/useIdentity';
export { WrappedRepositoryNavTabs as RepositoryNavTabs } from './RepositoryNavTabs';

import { Row } from '@/ui/primatives';
import * as Icon from '@/ui/Icon';
import * as Styled from './styled';

import { BaseHeader, HeaderProps } from '../BaseHeader';
import CopyTextControl from '@/ui/CopyTextControl';

type RepositoryHeaderProps = HeaderProps & {
  repository: IRepository;
};

const RepositoryPath: React.FC<{ repository: IRepository }> = ({ repository }) => (
  <Styled.RepositoryPath>
    <Row gap={5} alignItems="center">
      <Link
        className="repository-path link"
        href={`/${repository.namespace}`}
        data-ph-capture-attribute-resource-type="namespace"
      >
        {repository.namespace}
      </Link>
      <span className="repository-path separator">/</span>
    </Row>
    <Row gap={5} alignItems="center">
      <Link
        className="repository-path hover-link"
        style={{ display: 'inline-grid' }}
        href={`/${repository.namespace}/${repository.name}`}
        data-ph-capture-attribute-resource-type="repository"
      >
        <div className="text-line-truncate">{repository.name}</div>
      </Link>

      <CopyTextControl
        id="repository-path"
        value={`${repository.namespace}/${repository.name}`}
        event={{
          name: 'copied_repository_path',
          payload: { repository_namespace: repository.namespace, repository_name: repository.name },
        }}
      />

      {!repository.is_public && <Icon.Lock size="12" />}
    </Row>
  </Styled.RepositoryPath>
);

export const RepositoryHeader: React.FC<RepositoryHeaderProps> = ({
  children,
  currentUser,
  repository,
  variant,
  noUserContent,
  registrationDisabled,
  onLogout,
  ...rest
}) => {
  return (
    <BaseHeader
      currentUser={currentUser}
      contextPath={<RepositoryPath repository={repository} />}
      variant={variant}
      noUserContent={noUserContent}
      registrationDisabled={registrationDisabled}
      onLogout={onLogout}
      {...rest}
    >
      {children}
    </BaseHeader>
  );
};

const WrappedRepositoryHeader: React.FC<RepositoryHeaderProps> = ({
  children,
  currentUser,
  repository,
  variant,
  noUserContent,
  registrationDisabled,
  ...rest
}) => {
  const { logout } = Identity.useContainer();
  if (!repository) {
    return (
      <BaseHeader
        currentUser={currentUser}
        variant={variant}
        noUserContent={noUserContent}
        registrationDisabled={registrationDisabled}
        onLogout={logout}
        {...rest}
      />
    );
  }
  return (
    <RepositoryHeader
      currentUser={currentUser}
      repository={repository}
      variant={variant}
      noUserContent={noUserContent}
      registrationDisabled={registrationDisabled}
      onLogout={logout}
      {...rest}
    >
      {children}
    </RepositoryHeader>
  );
};

export default WrappedRepositoryHeader;
